import React, { useState } from 'react';
import { useCurrentUser } from '../contexts/AuthContext';
import { 
  Smartphone, 
  Shield, 
  ChevronDown,
  ChevronUp,
  Zap,
  Globe
} from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { useTenant } from '../contexts/TenantContext';
import { NotificationPreferences } from '../components/notifications/NotificationPreferences';

export const NotificationSettingsPage = () => {
  const { brand } = useTenant();
  const { user, memberships = [] } = useCurrentUser();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);

  const safeMemberships = Array.isArray(memberships) ? memberships : [];

  const filteredOrgs = safeMemberships.filter(org => 
    (org.nodeName || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-12">
      <header className="mb-12">
        <h1 className="text-3xl font-black text-gray-900 dark:text-gray-100 tracking-tight">Notification Sovereignty</h1>
        <p className="text-gray-500 mt-2 font-mono uppercase tracking-widest text-[10px]">Manage your attention across the roster</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column: Global Controls */}
        <div className="space-y-6">
          <NotificationPreferences />
        </div>

        {/* Right Column: Organization Overrides */}
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2 px-4">
            <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400">Organization Sovereignty</h2>
            <div className="relative group">
              <input 
                type="text" 
                placeholder="Search your roster..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-white border-2 border-black dark:border-white rounded-none px-4 py-2 text-xs focus:ring-2 focus:ring-brand-primary/20 outline-none w-full md:w-64 transition-all dark:bg-slate-900"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300">
                <Globe size={14} />
              </div>
            </div>
          </div>

          {filteredOrgs.length === 0 ? (
            <div className="bg-white rounded-[2rem] p-12 text-center border-2 border-black dark:border-white border-dashed border-black dark:border-white dark:bg-slate-950">
              <p className="text-gray-400 text-sm font-medium">No organizations match your search.</p>
            </div>
          ) : filteredOrgs.map((org) => {
            const isExpanded = expandedOrg === org.id;

            return (
              <div 
                key={org.id}
                className={`bg-white rounded-[2rem] border-2 border-black dark:border-white transition-all overflow-hidden ${
                  isExpanded ? 'border-brand-primary ring-4 ring-brand-primary/5 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]' : 'border-black dark:border-white'
                }`}
              >
                {/* Org Header */}
                <div className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-none flex items-center justify-center font-black bg-brand-primary text-white`}>
                      {org.nodeName.charAt(0)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-gray-900 dark:text-white">{org.nodeName}</h3>
                        <Badge variant={org.tier === 'LOCAL' ? 'gray' : 'blue'} className="text-[8px] uppercase">
                          {org.tier}
                        </Badge>
                      </div>
                      <p className="text-[10px] text-gray-400 font-mono uppercase tracking-tight">
                        {isExpanded ? 'Configuring Specific Overrides' : 'Following Global Defaults or Custom Preferences'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => setExpandedOrg(isExpanded ? null : org.id)}
                      className="p-2 bg-white text-gray-400 rounded-none hover:bg-white transition-all font-bold text-[10px] flex items-center gap-1 dark:bg-slate-950"
                    >
                      {isExpanded ? 'CLOSE' : 'CONFIGURE'} {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                  </div>
                </div>

                {/* Expanded Controls */}
                {isExpanded && (
                  <div className="px-8 pb-8 pt-2 border-t border-gray-50 bg-white dark:bg-slate-950">
                    <NotificationPreferences nodeId={org.nodeId || org.id} />
                      
                    {(org.tier === 'LOCAL' || org.tier === 'FOUNDATION') && (
                      <div className="bg-amber-50 rounded-none p-4 mt-6 flex items-start gap-3 border-2 border-black dark:border-white border-amber-100">
                        <Zap size={14} className="text-amber-500 mt-0.5" />
                        <p className="text-[10px] text-amber-700 leading-relaxed font-medium">
                          <span className="font-bold">Notice:</span> Depending on your tier, SMS preferences could be overriden or disabled by the node administrators.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-gray-900 rounded-[3rem] p-12 mt-12 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 p-12 opacity-10">
          <Smartphone size={240} />
        </div>
        <div className="relative z-10 max-w-xl">
          <h2 className="text-2xl font-black mb-4">Attention Sovereignty</h2>
          <p className="text-gray-400 text-sm leading-relaxed mb-6">
            Mandatory emergency alerts (Pulse Critical) will always bypass muting settings. 
            By using this platform, you agree to receive essential safety and governance communications from your organization administrators.
          </p>
          <div className="flex items-center gap-4">
             <div className="w-12 h-12 bg-white/10 rounded-none flex items-center justify-center text-brand-secondary dark:bg-slate-900">
               <Shield size={24} />
             </div>
             <div className="text-[10px] font-mono uppercase tracking-widest text-gray-400">
               Secured via {brand?.name} Privacy Protocol 2.1
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};

